import React, { useState } from 'react';
import { Phone, X, Clock, Calendar, User, MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallbackModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    preferred_time: '',
    preferred_day: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API_URL}/api/callback-requests`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', preferred_time: '', preferred_day: '', message: '' });
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl text-[#1a5c3b]">
            <Phone className="w-6 h-6" />
            {t.callback.title}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {t.callback.description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="callback-name" className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#1a5c3b]" />
              {t.callback.nameLabel}
            </Label>
            <Input
              id="callback-name"
              data-testid="callback-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="John Doe"
              className="border-gray-300 focus:border-[#1a5c3b] focus:ring-[#1a5c3b]"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="callback-phone" className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#1a5c3b]" />
              {t.callback.phoneLabel}
            </Label>
            <Input
              id="callback-phone"
              data-testid="callback-phone-input"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              placeholder="514-123-4567"
              className="border-gray-300 focus:border-[#1a5c3b] focus:ring-[#1a5c3b]"
            />
          </div>

          {/* Preferred Day */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#1a5c3b]" />
              {t.callback.preferredDay}
            </Label>
            <Select
              value={formData.preferred_day}
              onValueChange={(value) => setFormData({ ...formData, preferred_day: value })}
              required
            >
              <SelectTrigger data-testid="callback-day-select" className="border-gray-300">
                <SelectValue placeholder={t.callback.preferredDay} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">{t.callback.today}</SelectItem>
                <SelectItem value="tomorrow">{t.callback.tomorrow}</SelectItem>
                <SelectItem value="this_week">{t.callback.thisWeek}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preferred Time */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#1a5c3b]" />
              {t.callback.preferredTime}
            </Label>
            <Select
              value={formData.preferred_time}
              onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
              required
            >
              <SelectTrigger data-testid="callback-time-select" className="border-gray-300">
                <SelectValue placeholder={t.callback.preferredTime} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="morning">{t.callback.morning}</SelectItem>
                <SelectItem value="afternoon">{t.callback.afternoon}</SelectItem>
                <SelectItem value="evening">{t.callback.evening}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="callback-message" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#1a5c3b]" />
              {t.callback.message}
            </Label>
            <Textarea
              id="callback-message"
              data-testid="callback-message-input"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="..."
              className="border-gray-300 focus:border-[#1a5c3b] focus:ring-[#1a5c3b] min-h-[80px]"
            />
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">
              {t.callback.success}
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {t.callback.error}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="callback-submit-btn"
            disabled={isSubmitting}
            className="w-full bg-[#1a5c3b] hover:bg-[#165432] text-white py-3"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t.callback.submitting}
              </>
            ) : (
              <>
                <Phone className="w-4 h-4 mr-2" />
                {t.callback.submit}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CallbackModal;
